import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Descriptions, Table, Typography, Tag, Space, Spin, Tabs, Card, Alert, Timeline, Button } from 'antd';
import { CustomerOrder } from '../types';
import { customerOrderAPI } from '../services/api.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';

const { Text } = Typography;
const { TabPane } = Tabs;

// Etichette e colori per gli eventi della cronologia (audit-log)
const EVENT_LABELS: { [key: string]: string } = {
  CREATE: 'Ordine creato',
  UPDATE: 'Modifica',
  STATUS_CHANGE: 'Cambio stato',
  DELETE: 'Eliminazione',
  PRODUCT_ADDED: 'Prodotto aggiunto',
  PRODUCT_REMOVED: 'Prodotto rimosso',
  PRODUCT_MODIFIED: 'Prodotto modificato',
  RESERVATION_ADDED: 'Prenotazione aggiunta',
  RESERVATION_REMOVED: 'Prenotazione rimossa',
  RESERVATION_MODIFIED: 'Prenotazione modificata',
  RECEIVE: 'Merce ricevuta',
  CONVERT: 'Conversione',
};

const getEventLabel = (eventType: string) => EVENT_LABELS[eventType] || eventType;

const getEventColor = (eventType: string) => {
  if (['CREATE', 'PRODUCT_ADDED', 'RESERVATION_ADDED', 'RECEIVE'].includes(eventType)) return 'green';
  if (['DELETE', 'PRODUCT_REMOVED', 'RESERVATION_REMOVED'].includes(eventType)) return 'red';
  if (['STATUS_CHANGE', 'CONVERT'].includes(eventType)) return 'blue';
  return 'gray';
};

interface OrderDetailsModalProps {
  order: CustomerOrder | null;
  onClose: () => void;
}

interface CartItem {
  id: number;
  product_id: number;
  product_code: string;
  product_description: string;
  stock_id: number;
  stock_code: string;
  quantity_reserved: number;
  unit_price: number;
  total_price: number;
  source_type: 'available_stock' | 'in_production' | 'in_transit';
  udc_pallet: string | null;
  udc_collo: string | null;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [orderHistory, setOrderHistory] = useState<any[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  
  const formatProductDisplay = (product: any): string => {
    if (!product) return 'N/A';
    
    let display = product.internal_product_code || '';
    
    if (product.size) {
      display += `-${product.size}`;
    } else {
      display += '-';
    }
    
    if (product.color) {
      display += `-${product.color}`;
    } else {
      display += '-';
    }
    
    display += ' - ';
    
    if (product.client_product_code) {
      display += product.client_product_code;
    }
    
    display += ` - ${product.product_description || ''}`;
    
    return display;
  };
  
  const loadOrderDetails = useCallback(async () => {
    if (!order) return;
    
    setLoading(true);
    try {
      // Usa i dettagli già presenti nell'ordine se disponibili
      if (order.order_details && order.order_details.length > 0) {
        setOrderDetails(order.order_details);
      } else {
        // Altrimenti cerca di caricarli tramite API
        const details = await customerOrderAPI.getCustomerOrder(order.id);
        setOrderDetails(details.order_details || []);
      }
    } catch (error) {
      console.error('Error loading order details:', error);
      setOrderDetails([]);
    } finally {
      setLoading(false);
    }
  }, [order]);

  const loadOrderCart = useCallback(async () => {
    if (!order) return;
    try {
      const data = await customerOrderAPI.getOrderCart(order.id);
      setCartItems(data.cart_items || []);
    } catch (error) {
      console.error('Error loading order cart:', error);
      setCartItems([]);
    }
  }, [order]);

  const loadOrderHistory = useCallback(async () => {
    if (!order) return;

    try {
      const history = await customerOrderAPI.getCustomerOrderHistory(order.id);
      setOrderHistory(history || []);
    } catch (error) {
      console.error('Error loading order history:', error);
      setOrderHistory([]);
    }
  }, [order]);

  useEffect(() => {
    if (order) {
      loadOrderDetails();
      loadOrderHistory();
      loadOrderCart();
    }
  }, [order, loadOrderDetails, loadOrderHistory, loadOrderCart]);

  const getStatusColor = (statusName: string) => {
    const colors: { [key: string]: string } = {
      'Nuovo': 'blue',
      'In Lavorazione': 'orange',
      'Confermato': 'green',
      'Pronto per Spedizione': 'cyan',
      'Spedito': 'purple',
      'Consegnato': 'lime',
      'Annullato': 'red',
    };
    return colors[statusName] || 'default';
  };

  const getSourceTypeLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'available_stock': return 'Disponibile';
      case 'in_transit': return 'In Transito';
      case 'in_production': return 'In Produzione';
      default: return sourceType;
    }
  };

  const getSourceTypeColor = (sourceType: string) => {
    switch (sourceType) {
      case 'available_stock': return 'green';
      case 'in_transit': return 'orange';
      case 'in_production': return 'blue';
      default: return 'default';
    }
  };

  const getProductReservations = (productId: number): CartItem[] => {
    return cartItems.filter(item => item.product_id === productId);
  };

  if (!order) return null;

  const columns = [
    {
      title: 'Prodotto',
      key: 'product_code',
      width: 300,
      ellipsis: {
        showTitle: false,
      },
      render: (_: any, record: any) => {
        const product = record.product;
        if (!product) return <Text>N/A</Text>;
        
        const formattedDisplay = formatProductDisplay(product);
        return <Text code strong title={formattedDisplay}>{formattedDisplay}</Text>;
      },
    },
    {
      title: 'Qta',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center' as const,
      render: (quantity: number) => <Text strong>{quantity}</Text>,
    },
    {
      title: 'Vendita Unit.',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => <Text>€ {price ? formatCurrency(price) : '0,0000'}</Text>,
    },
    {
      title: 'Totale',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 100,
      align: 'right' as const,
      render: (price: number) => <Text strong style={{ color: '#1890ff' }}>€ {price ? formatCurrency(price) : '0,0000'}</Text>,
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      key: 'notes',
      width: 120,
      ellipsis: {
        showTitle: false,
      },
      render: (notes: string) => 
        notes ? (
          <Text type="secondary" title={notes} style={{ maxWidth: 100 }}>
            {notes}
          </Text>
        ) : '-',
    },
  ];

  const totalAmount = orderDetails.reduce((sum, detail) => sum + (detail.total_price || 0), 0);
  return (
    <Modal
      title={
        <Space>
          <Text strong style={{ fontSize: 18 }}>Dettagli Ordine</Text>
          <Tag color="blue" style={{ fontSize: 14 }}>{order.order_number}</Tag>
        </Space>
      }
      open={true}
      onCancel={onClose}
      footer={null}
      width={1000}
      style={{ top: 20 }}
    >
      <Spin spinning={loading}>
        <Tabs defaultActiveKey="1">
          <TabPane tab="Informazioni Generali" key="1">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Numero Ordine" span={2}>
                <strong>{order.order_number}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Numero Ordine Cliente" span={2}>
                {order.customer_order_number || 'Non specificato'}
              </Descriptions.Item>
              <Descriptions.Item label="Cliente">
                {order.customer?.company || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Email Cliente">
                {order.customer?.email || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Data Ordine">
                {new Date(order.order_date).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Data Consegna Richiesta">
                {order.requested_delivery_date ? new Date(order.requested_delivery_date).toLocaleDateString() : 'Non specificata'}
              </Descriptions.Item>
              <Descriptions.Item label="Stato">
                <Tag color={getStatusColor(order.order_status?.status_name || '')}>
                  {order.order_status?.status_name || 'N/A'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Importo Totale" span={2}>
                <strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  € {order.total_amount ? formatCurrency(order.total_amount) : formatCurrency(totalAmount)}
                </strong>
              </Descriptions.Item>
              {order.shipping_destination && (
                <Descriptions.Item label="Destinazione Spedizione" span={2}>
                  <Space direction="vertical" size="small">
                    <Text strong>{order.shipping_destination.name}</Text>
                    <Text type="secondary">{order.shipping_destination.address}</Text>
                  </Space>
                </Descriptions.Item>
              )}
              {(order.ddt_number || order.invoice_number || order.invoice_date || order.t1_number || order.t1_date) && (
                <Descriptions.Item label="Dati di Fatturazione" span={2}>
                  <Space direction="vertical" size={0}>
                    {order.ddt_number && <Text>DDT: {order.ddt_number}</Text>}
                    {(order.invoice_number || order.invoice_date) && (
                      <Text>
                        Fattura: {order.invoice_number || '—'}
                        {order.invoice_date ? ` del ${new Date(order.invoice_date).toLocaleDateString()}` : ''}
                      </Text>
                    )}
                    {(order.t1_number || order.t1_date) && (
                      <Text>
                        T1: {order.t1_number || '—'}
                        {order.t1_date ? ` del ${new Date(order.t1_date).toLocaleDateString()}` : ''}
                      </Text>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
              {order.notes && (
                <Descriptions.Item label="Note" span={2}>
                  {order.notes}
                </Descriptions.Item>
              )}
            </Descriptions>
          </TabPane>

          <TabPane tab="Dettagli Prodotti" key="2">
            <Table
              dataSource={orderDetails}
              columns={columns}
              pagination={false}
              rowKey="id"
              size="small"
              expandable={{
                expandedRowRender: (record: any) => {
                  const reservations = getProductReservations(record.product_id);
                  if (reservations.length === 0) {
                    return (
                      <div style={{ paddingLeft: 24, color: '#999', fontStyle: 'italic' }}>
                        Nessuna prenotazione per questo prodotto
                      </div>
                    );
                  }
                  const totalReserved = reservations.reduce((sum, r) => sum + r.quantity_reserved, 0);
                  const orderedQty = record.quantity || 0;
                  const difference = orderedQty - totalReserved;
                  return (
                    <div style={{ paddingLeft: 24 }}>
                      <Table
                        dataSource={reservations}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        showHeader={true}
                        columns={[
                          {
                            title: 'UDC Pallet',
                            dataIndex: 'udc_pallet',
                            key: 'udc_pallet',
                            width: 120,
                            render: (val: string) => val ? <Tag color="blue">{val}</Tag> : '-',
                          },
                          {
                            title: 'UDC Collo',
                            dataIndex: 'udc_collo',
                            key: 'udc_collo',
                            width: 120,
                            render: (val: string) => val ? <Tag color="cyan">{val}</Tag> : '-',
                          },
                          {
                            title: 'Fonte',
                            dataIndex: 'source_type',
                            key: 'source_type',
                            width: 120,
                            render: (val: string) => (
                              <Tag color={getSourceTypeColor(val)}>{getSourceTypeLabel(val)}</Tag>
                            ),
                          },
                          {
                            title: 'Qta Prenotata',
                            dataIndex: 'quantity_reserved',
                            key: 'quantity_reserved',
                            width: 110,
                            align: 'center' as const,
                            render: (val: number) => <Text strong>{val}</Text>,
                          },
                          {
                            title: 'Stock Code',
                            dataIndex: 'stock_code',
                            key: 'stock_code',
                            width: 120,
                            render: (val: string) => val || '-',
                          },
                        ]}
                      />
                      <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
                        <Text strong>Totale prenotato: {totalReserved}</Text>
                        {difference > 0 && (
                          <Tag color="red">Mancanti: {difference}</Tag>
                        )}
                        {difference === 0 && (
                          <Tag color="green">Completo</Tag>
                        )}
                        {difference < 0 && (
                          <Tag color="orange">Eccedenza: {Math.abs(difference)}</Tag>
                        )}
                      </div>
                    </div>
                  );
                },
                defaultExpandAllRows: true,
              }}
            />

            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <strong>Totale Ordine: € {order.total_amount ? formatCurrency(order.total_amount) : formatCurrency(totalAmount)}</strong>
            </div>
          </TabPane>          <TabPane tab="Informazioni Cliente" key="3">
            {order.customer ? (
              <Card size="small">
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="Nome Azienda">
                    {order.customer.company}
                  </Descriptions.Item>
                  {order.customer.email && (
                    <Descriptions.Item label="Email">
                      {order.customer.email}
                    </Descriptions.Item>
                  )}
                  {order.customer.phone && (
                    <Descriptions.Item label="Telefono">
                      {order.customer.phone}
                    </Descriptions.Item>
                  )}
                  {order.customer.address && (
                    <Descriptions.Item label="Indirizzo">
                      {order.customer.address}
                      {order.customer.city && `, ${order.customer.city}`}
                      {order.customer.postal_code && ` ${order.customer.postal_code}`}
                      {order.customer.country && `, ${order.customer.country}`}
                    </Descriptions.Item>
                  )}
                  {order.customer.notes && (
                    <Descriptions.Item label="Note Cliente">
                      {order.customer.notes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            ) : (
              <Alert message="Informazioni cliente non disponibili" type="info" />
            )}
          </TabPane>

          <TabPane tab="Cronologia" key="4">
            {orderHistory.length === 0 ? (
              <Alert message="Nessuna operazione registrata per questo ordine" type="info" />
            ) : (
              <Timeline>
                {orderHistory.map((event, index) => (
                  <Timeline.Item key={event.id ?? index} color={getEventColor(event.event_type)}>
                    <div>
                      <strong>{getEventLabel(event.event_type)}</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {new Date(event.created_at).toLocaleString()}
                      </div>
                      {event.description && (
                        <div style={{ marginTop: 4 }}>{event.description}</div>
                      )}
                      {event.user && (
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          da {event.user.username}
                        </div>
                      )}
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </TabPane>
        </Tabs>

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </Spin>
    </Modal>
  );
};

export default OrderDetailsModal;